/**
 * Column-alignment detection for markdown tables. Walks the hast AST react-
 * markdown hands us, samples body cells, and decides per-column whether to
 * right-align (numeric) or left-align (text). Used by MessageContent.tsx
 * to attach `data-align` to each th/td.
 */

// Matches a numeric cell: optional sign, digits/commas, optional decimal,
// optional %. Trailing content is allowed only if it's non-word (emoji /
// symbols) — so "+0.35 ⬆️" classifies as numeric but "522 mentions" doesn't.
export const NUMERIC_RE = /^[+-]?[\d,]+(\.\d+)?%?(\s+[^\w]+)?$/

export const getNodeText = (node: unknown): string => {
  if (!node || typeof node !== 'object') return ''
  const n = node as { type?: string; value?: string; children?: unknown[] }
  if (n.type === 'text') return n.value ?? ''
  if (Array.isArray(n.children)) return n.children.map(getNodeText).join('')
  return ''
}

// ≥50% numeric body cells → column right-aligns. A single annotated cell
// shouldn't tip the column.
export const computeColumnAligns = (
  tableNode: unknown,
): ('left' | 'right')[] => {
  const node = tableNode as
    | { children?: Array<{ tagName?: string; children?: unknown[] }> }
    | undefined
  const tbody = node?.children?.find((c) => c.tagName === 'tbody') as
    | { children?: Array<{ tagName?: string; children?: unknown[] }> }
    | undefined
  const bodyRows = tbody?.children?.filter((c) => c.tagName === 'tr') ?? []
  if (bodyRows.length === 0) return []
  const firstRowCells =
    (bodyRows[0]?.children as Array<{ tagName?: string }> | undefined)?.filter(
      (c) => c.tagName === 'td',
    ).length ?? 0
  return Array.from({ length: firstRowCells }, (_, colIndex) => {
    let numericCount = 0
    let totalCount = 0
    for (const row of bodyRows) {
      const cells = (
        row.children as
          | Array<{ tagName?: string; children?: unknown[] }>
          | undefined
      )?.filter((c) => c.tagName === 'td')
      const cell = cells?.[colIndex]
      if (!cell) continue
      const text = getNodeText(cell).trim()
      if (!text) continue
      totalCount++
      if (NUMERIC_RE.test(text)) numericCount++
    }
    return totalCount > 0 && numericCount / totalCount >= 0.5 ? 'right' : 'left'
  })
}
